import { getColorByShortcut } from '../../../../../../utils/colors.js';

import { useCallback } from 'react';
import { onMouseWheel } from './scroll.js';
import {
  cloneSubtitle,
  filterTextMultipleForSearch,
  getNewWordForSearch,
  wait,
} from '../../common/functions.js';
import { cloneDeep, first, flatMapDeep, join, last, xor } from 'lodash';

export const useCallbacks = (state, props, onDispatch, subtitleRefs) => {

  
  // ***************************************************************************************
  // getNewScrollValue() permet d'avoir la position du scroll pour afficher le sous-titre au milieu
  // ***************************************************************************************
  const getNewScrollValue = useCallback(
    (currentIndex) => {
      const subtitles = state.filtered
        ? state.subtitlesFiltered
        : state.subtitles;

      // Calculer la position du milieu de la zone visible
      const middle = Math.floor(state.nbVisibleLines / 2);

      // Centrer le sous-titre courant au milieu
      let newScrollValue = currentIndex - middle;

      // S'assurer que la valeur ne dépasse pas les limites
      const maxScrollValue = Math.max(
        0,
        subtitles.length - state.nbVisibleLines
      );

      // Limiter entre 0 et la valeur maximale
      newScrollValue = Math.max(0, Math.min(newScrollValue, maxScrollValue));

      return newScrollValue;
    },
    [state]
  );

  // ***************************************************************************************
  // onWheel() permet de scroller la liste des sous-titres
  // ***************************************************************************************
  const onWheel = useCallback(
    (event) => {
      const currentIndex = onMouseWheel(state, event);

       const currentSubtitle = state.filtered
        ? state.subtitlesFiltered[currentIndex]
         : state.subtitles[currentIndex];
      
      const scrollValue = getNewScrollValue(currentIndex);

      if (currentSubtitle) {
        onDispatch({ type: 'onChangeScrollValue', value: scrollValue }, () =>
          props.onMove(currentSubtitle.id, currentSubtitle.in)
        );
      }
    },
    [state, onDispatch, props]
  );

  // ***************************************************************************************
  // onHandleChangeScroll() permet d'aller directement à un point précis quand on clique sur le scroll
  // ***************************************************************************************
  const onHandleChangeScroll = useCallback(
    (event) => {
      let scrollValue = Number.parseInt(event.target.value);

      const { subtitles, subtitlesFiltered, filtered, nbVisibleLines, scroll } =
        state;

      const subs = filtered ? subtitlesFiltered : subtitles;
      const maxScrollValue = subs.length - nbVisibleLines;

      if (scrollValue === maxScrollValue) {
        const lastSubtitle = last(subs);

        if (lastSubtitle) {
          onDispatch(
            { type: 'onChangeScrollValue', value: scrollValue + 1 },
            () => props.onMove(lastSubtitle.id, lastSubtitle.in)
          );
        }

        return;
      }

      if (scrollValue === 0) {
        const firstSubtitle = first(subs);

        if (firstSubtitle) {
          onDispatch({ type: 'onChangeScrollValue', value: scrollValue }, () =>
            props.onMove(firstSubtitle.id, firstSubtitle.in)
          );
        }

        return;
      }

      const isScrollingDown = scrollValue > scroll.value;

      const middle = Math.floor(nbVisibleLines / 2);
      const startIndex = scrollValue;

      let nextIndex;
      if (isScrollingDown) {
        nextIndex = startIndex + nbVisibleLines - middle - 1;
      } else {
        nextIndex = startIndex + middle;
      }

      const currentSubtitle = subs[nextIndex];

      if (currentSubtitle) {
        onDispatch({ type: 'onChangeScrollValue', value: scrollValue }, () =>
          props.onMove(currentSubtitle.id, currentSubtitle.in)
        );
      }
    },
    [state, props, onDispatch]
  );

  // ***************************************************************************************
  // onChangePosition() permet de changer de position de sous-titres
  // ***************************************************************************************
  const onChangePosition = useCallback(
    async (index, indexWord) => {
      const subtitle = state.subtitles[index];
      props.onMove(subtitle.id, subtitle.in);

      await wait(100);

      const activeRef = subtitleRefs.current.find(
        (x) => x.subtitle.id === subtitle.id
      );
      if (activeRef) {
        activeRef.onGoToWord(indexWord);
      }
    },
    [state, props, subtitleRefs]
  );

  // ***************************************************************************************
  // onSelectSubtitles() permet de sélectionner des sous-titres
  // ***************************************************************************************
  const onSelectSubtitles = useCallback(
    (selected) => {
      const subtitles = state.subtitles;

      subtitles.forEach((subtitle) => {
        subtitle.selected = selected;
        subtitle.lines.forEach((line) => (line.selected = selected));
      });

      onDispatch(
        {
          type: 'onUpdateSubtitles',
          value: { subtitles },
        },
        () => {
          if (props.onSelectSubtitles) {
            props.onSelectSubtitles(subtitles);
          }
        }
      );

      
    },
    [onDispatch, state, props]
  );

  // ***************************************************************************************
  // onSelectRangeSubtitles() permet de sélectionner des sous-titres sur une plage
  // ***************************************************************************************
  const onSelectRangeSubtitles = useCallback(
    (min, max, isReverseSearch) => {
      if (state.filtered) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: 'Vous ne pouvez pas sélectionner les sous-titres si ceux-ci sont filtrés.',
              en: 'You cannot select subtitles if they are filtered.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
          },
        });

        return;
      }

      const onCancel = (id) => {
        const element = document.querySelector('#' + id);
        if (element) {
          element.focus();
        }

        onDispatch({ type: 'onClosePopup' });
      };

      if (min > state.subtitles.length - 1) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: `Vous devez sélectionner au minimum jusqu'à ${
                state.subtitles.length - 1
              }ème sous-titre`,
              en: `You must select at least up to ${
                state.subtitles.length - 1
              }th subtitle.`,
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel: () => onCancel('txtMin'),
          },
        });

        return;
      }

      if (max > state.subtitles.length - 1) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: `Vous n'êtes pas autorisé(e) à sélectionner au delà de la ${state.subtitles.length}ème sous-titre`,
              en: `You are not authorized to select beyond the ${state.subtitles.length}th subtitle.`,
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel: () => onCancel('txtMax'),
          },
        });

        return;
      }

      if (min > max) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: `Le minimum doit être inférieur au maximum.`,
              en: `Minimum must be less than maximum.`,
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel: () => onCancel('txtMin'),
          },
        });

        return;
      }

      const subtitles = state.subtitles;
      const nbSubtitles = subtitles.length;

      if (
        !min ||
        !max ||
        min === 0 ||
        max === 0 ||
        min > nbSubtitles ||
        max > nbSubtitles
      ) {
       subtitles
        .filter((x) => x.selected === true)
        .forEach((x) => {
          x.selected = false;
          x.lines.forEach((line) => {
            line.selected = false;
          });
        });

        onDispatch(
          {
            type: 'onUpdateSubtitles',
            value: { subtitles },
          },
          () => {
            if (props.onSelectSubtitles) {
              props.onSelectSubtitles(subtitles);
            }
          }
        );
        return;
      }

      subtitles
        .filter((x) => x.selected === true)
        .forEach((x) => {
          x.selected = false;
          x.lines.forEach((line) => {
            line.selected = false;
          });
        });

      let subtitlesToSelected = subtitles.filter(
        (x) => x.id >= min - 1 && x.id <= max - 1
      );

      const moveToSubtitle = first(subtitlesToSelected);

      if (isReverseSearch) {
        subtitlesToSelected = xor(subtitlesToSelected, subtitles);
      }

      if (!subtitlesToSelected.length) {
        return;
      }

      subtitlesToSelected.forEach((subtitle) => {
        subtitle.selected = true;
        subtitle.lines.forEach((line) => (line.selected = true));
      });


      onDispatch(
        {
          type: 'onUpdateSubtitles',
          value: { subtitles },
        },
        () => {
          if (props.onSelectSubtitles) {
            props.onSelectSubtitles(subtitles);
          }
        }
      );

      // move
      props.onMove(moveToSubtitle.id, moveToSubtitle.in);
    },
    [state, onDispatch, props]
  );

  // ***************************************************************************************
  // onDeleteSelectedSubtitles() permet de supprimer des sous-titres sélectionnés
  // ***************************************************************************************
  const onDeleteSelectedSubtitles = useCallback(() => {
    const deleteSubtitles = (subtitlesSelected) => {
      props.onApplyFilter({
        name: 'delete',
        subtitlesSelected: subtitlesSelected.map((x) => cloneSubtitle(x)),
      });

      onDispatch({
        type: 'onClosePopup',
      });
    };

    const subtitles = state.subtitles;

    const subtitlesSelected = subtitles.filter((x) => x.selected === true);

    if (!subtitlesSelected.length) {
      onDispatch({
        type: 'onShowPopup',
        value: {
          title: '',
          text: {
            fr: "Vous devez d'abord sélectionner les sous-titres avant de les supprimer.",
            en: 'You must first select the subtitles before deleting them.',
          },
          buttons: [],
          showIcon: true,
          icon: null,
        },
      });

      return;
    }

    onDispatch({
      type: 'onShowPopup',
      value: {
        title: {
          fr: 'Suppression des sous-titres sélectionnés',
          en: 'Deleting selected subtitles',
        },
        text: {
          fr: 'Êtes-vous sûr(e) de supprimer ces sous-titres ?',
          en: 'Are you sure you want to remove these subtitles?',
        },
        buttons: [
          {
            text: {
              fr: 'Oui',
              en: 'Yes',
            },
            onclick: () => {
              subtitlesSelected.forEach((subtitle) => {
                subtitle.selected = false;
                subtitle.lines.forEach((line) => (line.selected = false));
              });

              deleteSubtitles(subtitlesSelected);
            },
          },
          {
            text: {
              fr: 'Non',
              en: 'No',
            },
            onclick: () => {
              onDispatch({ type: 'onClosePopup' });
            },
          },
        ],
      },
    });
  }, [onDispatch, props, state]);

  // ***************************************************************************************
  // onSubtitleChecked() permet de sélectionner un sous-titre
  // ***************************************************************************************
  const onSubtitleChecked = useCallback(
    (id, checked) => {
      const { subtitles } = state;

      const subtitle = subtitles.find((x) => x.id === id);
      subtitle.selected = checked;
      subtitle.lines.forEach((line) => (line.selected = checked));

      onDispatch(
        {
          type: 'onUpdateSubtitles',
          value: { subtitles },
        },
        () => {
          if (props.onSelectSubtitles) {
            props.onSelectSubtitles(subtitles);
          }
        }
      );
    },
    [onDispatch, state, props]
  );

   const onSubtitleLineChecked = useCallback(
    (id, indexLine, selected) => {
      const { subtitles } = state;

      const subtitle = subtitles.find((x) => x.id === id);

      subtitle.lines[indexLine].selected = selected;
      subtitle.selected = subtitle.lines.some((x) => x.selected);

     onDispatch(
        {
          type: 'onUpdateSubtitles',
          value: { subtitles },
        },
        () => {
          if (props.onSelectSubtitles) {
            props.onSelectSubtitles(subtitles);
          }
        }
      );
    },
    [onDispatch, state, props]
  );


  // ***************************************************************************************
  // onSetColor() permet d'appliquer la couleur sur les sous-titres sélectionnées ou filtrés
  // ***************************************************************************************
  const onSetColor = useCallback(
    (data) => {
      const { subtitles, subtitlesFiltered, filtered } = state;

      let color = getColorByShortcut(data.key);
      if (color) {
        if (filtered) {
          subtitlesFiltered.forEach((subtitle) => {
            subtitle.lines.forEach((line) => {
              line.words.forEach((word) => {
                const found = state.search.wordsToSearch.find(
                  (w) =>
                    w.id === subtitle.id &&
                    w.in === subtitle.in &&
                    w.inWord === word.in &&
                    w.text === word.text
                );
                if (found) {
                  word.color = color.hex;
                }
              });
            });
          });

          props.onApplyFilter({
            name: 'colorFiltered',
            subtitlesFiltered: subtitlesFiltered.map((x) => cloneSubtitle(x)),
            color,
          });

          return;
        }

        const subtitlesSelected = cloneDeep(
          subtitles.filter((x) => x.selected === true)
        );

        if (!subtitlesSelected.length) {
          onDispatch({
            type: 'onShowPopup',
            value: {
              title: '',
              text: {
                fr: "Vous devez d'abord sélectionner les sous-titres avant d'appliquer la couleur.",
                en: 'You must first select the subtitles before applying the color.',
              },
              buttons: [],
              showIcon: true,
              icon: null,
            },
          });

          return;
        }

        props.onApplyFilter({ name: 'color', subtitlesSelected, color });
      }
    },
    [state, onDispatch, props]
  );

  // ***************************************************************************************
  // onDuplicateSubtitles() permet de dupliquer les sous-titres sélectionnés vers un autre endroit de la liste
  // ***************************************************************************************
  const onDuplicateSubtitles = useCallback(
    (duplicateIdTo) => {
      const { subtitles, filtered } = state;

      const onCancel = () => {
        const element = document.querySelector('#txtDuplication');
        if (element) {
          element.focus();
        }

        onDispatch({ type: 'onClosePopup' });
      };

      if (filtered) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: 'Vous ne pouvez pas dupliquer les sous-titres si ceux-ci sont filtrés.',
              en: 'You cannot duplicate subtitles if they are filtered.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      const selectedSubtitles = cloneDeep(
        subtitles.filter((x) => x.selected === true)
      );

      if (!selectedSubtitles.length) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: "Vous devez d'abord sélectionner les sous-titres avant de les dupliquer.",
              en: 'You must first select the subtitles before duplicating them.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      if (!duplicateIdTo) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: 'Vous devez renseigner vers quel numéro de sous-titre où vous souhaitez dupliquer les sous-titres sélectionnées.',
              en: 'Enter the subtitle number to which you wish to duplicate the selected subtitles.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      if (duplicateIdTo > subtitles.length - 1) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: `Vous n'êtes pas autorisé(e) à dupliquer au delà de la ${subtitles.length}ème sous-titre`,
              en: `You are not authorized to duplicate beyond the ${subtitles.length}th subtitle.`,
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      props.onApplyFilter({
        name: 'duplicate',
        selectedSubtitles,
        idTo: duplicateIdTo,
      });
    },
    [onDispatch, props, state]
  );

  // ***************************************************************************************
  // onUndoOrRedo() permet de revenir en arrière ou en avant (undo/redo)
  // ***************************************************************************************
  const onUndoOrRedo = useCallback(
    (key) => {
      let action = key === 'UNDO' ? last(props.undo) : first(props.redo);
      if (!action) {
        return;
      }

      const subtitles =
        key === 'UNDO' ? action.undo.subtitles : action.redo.subtitles;

      if (subtitles) {
        if (key === 'UNDO' && !action.redo) {
          const ids = new Set(subtitles.map((w) => w.id));

          const subtitlesToRedo = cloneDeep(
            state.subtitles
              .filter((x) => ids.has(x.id))
              .map((s) => cloneSubtitle(s))
          );

          if (action.undo.actionType === "OnDeleteSubtitle") {
            subtitlesToRedo.push(...action.undo.subtitles);
          }

          action.redo = {
            ...action.undo,
            subtitles: subtitlesToRedo,
          };
        }

        props.onUndoOrRedo(key, action);

        return;
      }

      const id = key === 'UNDO' ? action.undo.id : action.redo.id;

      const activeRef = subtitleRefs.current.find((x) => x.subtitle.id === id);
      if (activeRef) {
        activeRef.onUndoOrRedo(key);
      } else {
        const subtitle = state.subtitles.find((x) => x.id === id);
        if (subtitle) {
          props.onMove(subtitle.id, subtitle.in);
          setTimeout(() => onUndoOrRedo(key), 100);
        }
      }
    },
    [props, state, subtitleRefs]
  );

  // ***************************************************************************************
  // onSelectColor() permet de filtrer les sous-titres ayant la couleur
  // ***************************************************************************************
  const onSelectColor = useCallback(
    (data) => {
      if (!data) {
        return;
      }

      if (state.filtered) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: 'Vous ne pouvez pas sélectionner les sous-titres si ceux-ci sont filtrés.',
              en: 'You cannot select subtitles if they are filtered.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
          },
        });

        return;
      }

      let color = getColorByShortcut(data.key);

      const { subtitles } = state;

      const subtitlesColored = subtitles.filter((subtitle) => {
        const words = flatMapDeep(subtitle.lines.map((x) => x.words));

        if (words.some((w) => w.color.toUpperCase() === color.hex)) {
          subtitle.checked = true;
          return subtitle;
        }

        return null;
      });

      if (subtitlesColored.length) {
        const firstSub = first(subtitlesColored);
        props.onMove(firstSub.id, firstSub.in);
      } else {
        props.onMove(-1, null);
      }

      onDispatch({
        type: 'onFilteredSubtitles',
        value: { subtitlesFiltered: subtitlesColored },
      });
    },
    [state, onDispatch, props]
  );

  // ***************************************************************************************
  // onClearFilteredSubtitles() permet d'effacer les sous-titres filtrés
  // ***************************************************************************************
  const onClearFilteredSubtitles = useCallback(() => {
    onDispatch({ type: 'onClearFilteredSubtitles' });
  }, [onDispatch]);

  // ***************************************************************************************
  // onSelectAlign() permet de filtrer les sous-titres par alignement (centre, droite, gauche, manuel)
  // ***************************************************************************************
  const onSelectAlign = useCallback(
    (align) => {
      if (!state.subtitles.length || align === null || isNaN(align)) {
        return;
      }

      const { subtitles } = state;

      const subsAligned = subtitles.filter((sub) => {
        if (sub.lines.some((l) => l.align === align)) {
          return sub;
        }

        return null;
      });

      onDispatch({
        type: 'onFilteredSubtitles',
        value: { subtitlesFiltered: subsAligned },
      });
    },
    [state, onDispatch]
  );

  // ***************************************************************************************
  // onSearchWordOnSubtitles() permet de filtrer les sous-titres ayant le mot
  // ***************************************************************************************
  const onSearchWordOnSubtitles = useCallback(
    async (text, caseSensitive, wholeWord) => {
      if (!state.subtitles.length) {
        return;
      }

      if (!text || text.length === 0) {
        onClearFilteredSubtitles();
        return;
      }

      const filterText = (callback) => {
        let wordsToSearch = [];

        const subtitlesFiltered = state.subtitles.filter((subtitle) => {
          const words = flatMapDeep(subtitle.lines.map((x) => x.words));

          const wordsFounded = words.filter(callback);

          if (wordsFounded.length) {
            wordsFounded.forEach((word) => {
              const index = words.indexOf(word);

              const newWord = getNewWordForSearch(
                caseSensitive,
                wholeWord,
                index,
                word,
                text
              );

              wordsToSearch.push({
                id: subtitle.id,
                in: subtitle.in,
                words: [newWord],
              });
            });

            return subtitle;
          }

          return null;
        });

        return { subtitlesFiltered, wordsToSearch };
      };

      const filterTextMultiple = (text) => {
        let wordsToSearch = [];

        const subtitlesFiltered = state.subtitles.filter((subtitle) => {
          const words = flatMapDeep(subtitle.lines.map((x) => x.words));

          const sentence = join(
            words.map((x) => x.text),
            ' '
          );

          if (sentence.toLowerCase().includes(text.toLowerCase())) {
            const wordsToAdd = filterTextMultipleForSearch(
              text,
              subtitle,
              words,
              caseSensitive
            );

            if (wordsToAdd.length) {
              wordsToSearch.push(...wordsToAdd);
              return subtitle;
            }
          }

          return null;
        });

        return { subtitlesFiltered, wordsToSearch };
      };

      let subtitlesFiltered = [];
      let wordsToSearch = [];

      // Recherche sur plusieurs mots
      const searchWords = text.trim().split(' ');
      if (searchWords.length > 1 && !wholeWord) {
        const result = filterTextMultiple(text);

        if (result.subtitlesFiltered.length) {
          subtitlesFiltered.push(...result.subtitlesFiltered);
          wordsToSearch.push(...result.wordsToSearch);
        }
      }

      // Recherche sur un seul mot
      let result;
      if (caseSensitive) {
        if (wholeWord) {
          result = filterText((x) => x.text === text);
        } else {
          result = filterText((x) => x.text.includes(text));
        }
      } else if (wholeWord) {
        result = filterText((x) => x.text.toLowerCase() === text.toLowerCase());
      } else {
        result = filterText((x) =>
          x.text.toLowerCase().includes(text.toLowerCase())
        );
      }

      if (result.subtitlesFiltered.length) {
        // Éviter les doublons dans subtitlesFiltered et wordsToSearch
        result.subtitlesFiltered.forEach((sub) => {
          if (!subtitlesFiltered.some((s) => s.id === sub.id)) {
            subtitlesFiltered.push(sub);
          }
        });
        result.wordsToSearch.forEach((wordObj) => {
          if (
            !wordsToSearch.some(
              (w) => w.id === wordObj.id && w.in === wordObj.in
            )
          ) {
            wordsToSearch.push(wordObj);
          }
        });
      }

      onDispatch({
        type: 'onSearchWordAndFilteredSubtitles',
        value: {
          subtitlesFiltered,
          wordsToSearch,
          text,
          caseSensitive,
          wholeWord,
        },
      });
    },
    [onDispatch, onClearFilteredSubtitles, state.subtitles]
  );

  // ***************************************************************************************
  // onCutAndPasteSubtitles() permet de couper coller une plage de sous-titres vers un autre endroit de la liste
  // ***************************************************************************************
  const onCutAndPasteSubtitles = useCallback(
    (cutAndPasteTo) => {
      const { subtitles, filtered } = state;

      const onCancel = () => {
        const element = document.querySelector('#txtCutPaste');
        if (element) {
          element.focus();
        }

        onDispatch({ type: 'onClosePopup' });
      };

      if (filtered) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: 'Vous ne pouvez pas couper et coller les sous-titres si ceux-ci sont filtrés.',
              en: 'You cannot cut and paste subtitles if they are filtered.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      const selectedSubtitles = cloneDeep(
        subtitles.filter((x) => x.selected === true)
      );

      if (!selectedSubtitles.length) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: "Vous devez d'abord sélectionner les sous-titres avant de les couper coller.",
              en: 'You must first select the subtitles before cut and paste them.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      if (!cutAndPasteTo) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: 'Vous devez renseigner vers quel numéro de sous-titre où vous souhaitez coller les sous-titres sélectionnées.',
              en: 'Enter the subtitle number to which you wish to paste the selected subtitles.',
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      if (cutAndPasteTo > subtitles.length - 1) {
        onDispatch({
          type: 'onShowPopup',
          value: {
            title: '',
            text: {
              fr: `Vous n'êtes pas autorisé(e) à couper coller au delà de la ${subtitles.length}ème sous-titre`,
              en: `You are not authorized to cut and paste beyond the ${subtitles.length}th subtitle.`,
            },
            buttons: [],
            showIcon: true,
            icon: null,
            onCancel,
          },
        });

        return;
      }

      props.onApplyFilter({
        name: 'cutAndPaste',
        selectedSubtitles,
        idTo: cutAndPasteTo,
      });
    },
    [onDispatch, props, state]
  );

  // ***************************************************************************************
  // onRefresh() permet de rafraichir la liste des sous-titres
  // ***************************************************************************************
  const onRefreshListSubtitlesCanvas = useCallback(() => {
    subtitleRefs.current.forEach((sub) => sub.onRefreshCanvas());
  }, [subtitleRefs]);

  return {
    onWheel,
    onHandleChangeScroll,
    onChangePosition,
    onSelectSubtitles,
    onSelectRangeSubtitles,
    onDeleteSelectedSubtitles,
    onSubtitleChecked,
    onSubtitleLineChecked,
    onSetColor,
    onDuplicateSubtitles,
    onUndoOrRedo,
    onSelectColor,
    onClearFilteredSubtitles,
    onSelectAlign,
    onSearchWordOnSubtitles,
    onCutAndPasteSubtitles,
    onRefreshListSubtitlesCanvas,
    getNewScrollValue
  };
};
